/* Special Prices, shopped by department.

   The cards arrive complete from Liquid — label, link, position — because the
   subcategories are tags on the department's collection and the menu order is
   known server-side. Two things are not knowable there: how many products sit
   behind a tag, and which photograph should stand for it. A page template
   cannot filter a collection by tag in Liquid, so both come from the probe
   endpoint at templates/collection.bs-subcat.liquid, one request per card.

   Work is done a tab at a time, when the tab is first opened. A department
   with six tabs would otherwise fire fifty requests before a shopper has
   looked at any of them. */
(function () {
  'use strict';

  var VIEW = 'bs-subcat';
  var CONCURRENCY = 6;
  /* The storefront rate-limits when a tab probes every tag at once. A 429 is
     transient, so it is retried rather than believed. */
  var RETRIES = 3;
  var STORE_KEY = 'bs-spdept';
  var SCROLL_SHARE = 0.85;

  /* Two layers, because they fail differently: the in-memory copy is always
     there, and sessionStorage carries counts across a page load but throws in
     private mode. */
  var memo = {};

  function store() {
    try {
      return window.sessionStorage;
    } catch (e) {
      return null;
    }
  }

  function cacheGet(key) {
    if (key in memo) return memo[key];
    var s = store();
    if (!s) return undefined;
    try {
      var raw = s.getItem(STORE_KEY + ':' + key);
      if (raw === null) return undefined;
      memo[key] = JSON.parse(raw);
      return memo[key];
    } catch (e) {
      return undefined;
    }
  }

  function cacheSet(key, value) {
    memo[key] = value;
    var s = store();
    if (!s) return;
    try {
      s.setItem(STORE_KEY + ':' + key, JSON.stringify(value));
    } catch (e) {
      /* Quota or private mode — the in-memory copy still serves this page. */
    }
  }

  /* Both forms come from Liquid already translated — see the section's
     data-count-* attributes. Arabic does not simply append a word to a
     numeral, so the plural is a template with the number slotted in rather
     than a number with a word after it. */
  function countLabel(root, n) {
    if (n === 1) {
      return (root && root.getAttribute('data-count-one')) || '1 item';
    }
    var many = (root && root.getAttribute('data-count-many')) || '__n__ items';
    return many.replace('__n__', n);
  }

  function list(root, selector) {
    return Array.prototype.slice.call(root.querySelectorAll(selector));
  }

  /* One photograph, one key, whatever URL it arrived as. A pin is typed as a
     cdn.shopify.com path while the probe returns the same file on the shop's
     own domain, and both carry ?v= and ?width= that differ by caller — so the
     filename is the only part the two forms agree on. Product id wins when
     there is one: a product ships several files, and matching those on
     filename alone would let the same item through under each of them. */
  function imageKey(id, src) {
    if (id != null) return 'p' + id;
    if (!src) return 'f';
    return 'f' + src.split('?')[0].split('/').pop().toLowerCase();
  }

  /* Runs jobs a few at a time and reports each as it lands, so a rail fills in
     progressively rather than waiting on the slowest tag. */
  function pool(jobs, limit) {
    var next = 0;
    function take() {
      if (next >= jobs.length) return Promise.resolve();
      var job = jobs[next++];
      return job().catch(function () {}).then(take);
    }
    var runners = [];
    for (var i = 0; i < Math.min(limit, jobs.length); i++) runners.push(take());
    return Promise.all(runners);
  }

  function probe(collectionUrl, handle) {
    var key = collectionUrl + '/' + handle;
    var cached = cacheGet(key);
    if (cached !== undefined) return Promise.resolve(cached);

    /* No Accept: application/json here on purpose. Asking a collection URL for
       JSON makes the storefront answer with its own built-in collection
       payload and ignore ?view= entirely, so the alternate template that
       carries the count never runs. */
    var url = collectionUrl + '/' + handle + '?view=' + VIEW;

    /* Backs off further each time, with jitter: a whole tab is retrying at
       once, and without the jitter every card would come back in the same
       instant and be rate-limited together a second time. */
    function backoff(left) {
      var wait = 350 * Math.pow(2, RETRIES - left) + Math.random() * 250;
      return new Promise(function (resolve) {
        window.setTimeout(resolve, wait);
      });
    }

    function attempt(left) {
      return fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } }).then(
        function (response) {
          /* 429 and 5xx are the storefront asking to be left alone for a
             moment. They say nothing about this category, so they must not
             reach the caller as an answer. A 404 is an answer — the tag URL
             does not exist — and is not worth retrying. */
          if (response.status === 429 || response.status >= 500) {
            if (left > 0) {
              return backoff(left).then(function () {
                return attempt(left - 1);
              });
            }
            throw new Error('HTTP ' + response.status);
          }
          if (!response.ok) throw new Error('HTTP ' + response.status);
          return response.json().then(function (data) {
            var result = {
              count: typeof data.count === 'number' ? data.count : 0,
              images: Array.isArray(data.images) ? data.images : []
            };
            cacheSet(key, result);
            return result;
          });
        },
        function (networkError) {
          if (left > 0) {
            return backoff(left).then(function () {
              return attempt(left - 1);
            });
          }
          throw networkError;
        }
      );
    }

    return attempt(RETRIES);
  }

  function absoluteImageUrl(src) {
    if (!src) return '';
    if (src.indexOf('//') === 0) return window.location.protocol + src;
    if (src.charAt(0) === '/') return window.location.origin + src;
    return src;
  }

  /* ---- Filling a card ------------------------------------------------- */

  /* How much of an image is actually dark. On the tinted ground a pale product
     all but disappears once the blend has run — an ivory knit on #EFEFEF is
     barely darker than the plate it sits on — so a candidate is only worth
     using if enough of its pixels sit well below the ground.

     Measured on a 24px copy: one tiny request, and enough to tell an ivory
     knit from a black blazer. Shopify's CDN allows cross-origin reads; a
     tainted canvas or a blocked request returns null and the caller falls back
     to document order. */
  var INK_LEVEL = 200;
  var INK_ENOUGH = 0.1;
  /* How many of a category's products compete for its plate, and how much
     richer a rival has to be to displace the lead product. The margin keeps
     the lead product when the difference is too small to see. */
  var RANK_POOL = 5;
  var RANK_MARGIN = 0.04;
  var inkCache = {};

  /* Kept in the session cache too, keyed by file, so the next page view
     ranks from memory instead of re-downloading every thumbnail. A failed
     read (null) is not stored: it may succeed next time. */
  function inkiness(src) {
    if (src in inkCache) return Promise.resolve(inkCache[src]);
    var storeKey = 'ink' + imageKey(null, src);
    var stored = cacheGet(storeKey);
    if (typeof stored === 'number') {
      inkCache[src] = stored;
      return Promise.resolve(stored);
    }
    return new Promise(function (resolve) {
      var small = absoluteImageUrl(src).replace(/([?&]width=)\d+/, '$1' + 24);
      var img = new Image();
      var done = function (value) {
        inkCache[src] = value;
        if (typeof value === 'number') cacheSet(storeKey, Math.round(value * 1000) / 1000);
        resolve(value);
      };
      img.crossOrigin = 'anonymous';
      img.onerror = function () { done(null); };
      img.onload = function () {
        try {
          var canvas = document.createElement('canvas');
          canvas.width = img.naturalWidth;
          canvas.height = img.naturalHeight;
          var ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);
          var px = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
          var dark = 0;
          var counted = 0;
          for (var i = 0; i < px.length; i += 4) {
            if (px[i + 3] < 16) continue;
            counted++;
            if (0.2126 * px[i] + 0.7152 * px[i + 1] + 0.0722 * px[i + 2] < INK_LEVEL) dark++;
          }
          done(counted ? dark / counted : 0);
        } catch (e) {
          done(null);
        }
      };
      img.src = small;
    });
  }

  /* Two jobs at once. Neighbouring subcategories share products — one spinner
     is the lead item of Cabin, Large, Soft and Extra Large Luggage alike — so
     a product already on screen is skipped. Identity is the product id, not
     the image URL: the same suitcase ships several photo files, and matching
     on URL let it through four times.

     Of what is left, the first RANK_POOL candidates are measured and the one
     with the most real colour mass leads: a bold, saturated or dark piece
     reads at thumbnail size on a sale page, where a white sleepsuit or an
     ivory knit on the pale plate reads as an empty card. The lead product
     keeps the plate unless a rival is clearly richer (RANK_MARGIN). If every
     candidate is pale, the best of them is used rather than leaving the plate
     empty, and the card is marked so the CSS can push the photograph harder. */
  function pickImage(seen, data, initialImage) {
    var images = (data.images || []).slice(0, 12);
    if (!images.length) return Promise.resolve(null);

    var key = function (image) {
      return image && (image.id != null ? 'p' + image.id : imageKey(null, image.src));
    };

    var fileKey = function (image) {
      return imageKey(null, image && image.src);
    };

    /* A pinned card claims its photograph by filename, because a pin is a bare
       URL with no product id behind it. A candidate therefore has to be tested
       both ways: its product may be unclaimed while the very same photo file
       is already pinned to a sibling. */
    var taken = function (image) {
      return !!(seen[key(image)] || seen[fileKey(image)]);
    };

    var claim = function (image) {
      seen[key(image)] = true;
      seen[fileKey(image)] = true;
    };

    var release = function (image) {
      delete seen[key(image)];
      delete seen[fileKey(image)];
    };

    /* Claim synchronously, before anything is measured. Every card in a rail
       calls this in the same tick, so a claim made only once the darkness
       check resolved would come far too late — every Luggage card would
       already have chosen the same suitcase. */
    var chosen = initialImage || null;
    if (chosen) {
      claim(chosen);
    } else {
      for (var i = 0; i < images.length; i++) {
        if (!taken(images[i])) {
          chosen = images[i];
          claim(chosen);
          break;
        }
      }
    }
    /* Every candidate is spoken for: repeat rather than leave a blank plate. */
    if (!chosen) return Promise.resolve({ image: images[0], ink: null });

    var swap = function (from, to, ink) {
      release(from);
      claim(to);
      return { image: to, ink: ink };
    };

    var rivals = images.filter(function (image) {
      return image !== chosen && !taken(image);
    }).slice(0, RANK_POOL - 1);

    /* Measured together: a serial walk took long enough for shoppers (and
       Google) to capture every card on its first image before it finished. */
    return Promise.all([chosen].concat(rivals).map(function (candidate) {
      return inkiness(candidate.src).then(function (value) {
        return { image: candidate, ink: value };
      });
    })).then(function (measured) {
      var lead = measured[0];
      var best = lead;
      for (var m = 1; m < measured.length; m++) {
        var option = measured[m];
        if (option.ink === null) continue;
        var bar = best.ink === null ? -1 : best.ink + (best === lead ? RANK_MARGIN : 0);
        /* A sibling may have claimed it while this card was measuring. */
        if (option.ink > bar && !taken(option.image)) best = option;
      }
      if (best !== lead) return swap(chosen, best.image, best.ink);
      return { image: chosen, ink: lead.ink };
    });
  }

  /* ---- Shoes: one direction, one baseline ---------------------------- */

  /* Shoe photographs are side views, but every brand frames them its own way:
     one sneaker sits low and small, the next loafer fills the top half, and
     most of this catalogue shoots the toe pointing left, so a footwear row
     read as shoes of every size walking backwards off the page.

     So a footwear card is framed from the shoe itself rather than from the
     file. The shoe's outline is measured on a 96px copy; the photograph is
     then scaled so every shoe is the same width, set on the same baseline,
     and mirrored when its toe points against the reading direction (right in
     English, left in Arabic). A shoe mirrored is still a convincing shoe - it
     simply becomes the other foot.

     The toe is found from the outline too: in a side view the heel end stands
     taller than the toe; when both ends are level the shoe is aligned but not
     flipped. Anything not wide enough to be a side view (a pair shot from
     above, a boot from the front) keeps the ordinary treatment. The plate's darken blend over its light ground means
     a photograph shrunk inside it shows no edge of its own. */
  var SHOE_WORDS = /(^|-)(shoes?|sneakers?|trainers?|loafers?|sandals?|flats|ballet|ballerinas|boots?|heels|pumps|espadrilles?|moccasins?|slides|slippers?|footwear|oxfords?|drivers|mules?|derby|brogues?)(-|$)/;
  var SHOE_WIDTH = 0.84;     // share of the plate's width
  var SHOE_MAX_HEIGHT = 0.5; // share of the plate's height, for tall boots
  var SHOE_BASELINE = 0.8;   // where the sole sits, down the plate
  var PLATE_RATIO = 4 / 3;   // height / width, as .bs-spdept__media
  var shoeCache = {};

  function isFootwear(card) {
    return SHOE_WORDS.test(String(card.getAttribute('data-handle') || '').toLowerCase());
  }

  function smallCopy(src, width) {
    var url = absoluteImageUrl(src);
    if (/[?&]width=\d+/.test(url)) return url.replace(/([?&]width=)\d+/, '$1' + width);
    if (/_\d+x\./.test(url)) return url.replace(/_\d+x\./, '_' + width + 'x.');
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  /* { aspect, box: [x0, y0, x1, y1] as shares of the image, toe: 'left' |
     'right' | null } for a side view, otherwise null. */
  function shoeGeometry(src) {
    var key = 'shoe2' + imageKey(null, src);
    if (key in shoeCache) return Promise.resolve(shoeCache[key]);
    var stored = cacheGet(key);
    if (stored !== undefined) {
      shoeCache[key] = stored;
      return Promise.resolve(stored);
    }
    return new Promise(function (resolve) {
      var img = new Image();
      var done = function (value, keep) {
        shoeCache[key] = value;
        if (keep) cacheSet(key, value);
        resolve(value);
      };
      img.crossOrigin = 'anonymous';
      img.onerror = function () { done(null, false); };
      img.onload = function () {
        try {
          var w = img.naturalWidth;
          var h = img.naturalHeight;
          var canvas = document.createElement('canvas');
          canvas.width = w;
          canvas.height = h;
          var ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);
          var px = ctx.getImageData(0, 0, w, h).data;
          // The photograph's own ground: the average of its four corners.
          var ground = [0, 0, 0];
          [[0, 0], [w - 1, 0], [0, h - 1], [w - 1, h - 1]].forEach(function (pt) {
            var o = (pt[1] * w + pt[0]) * 4;
            ground[0] += px[o] / 4;
            ground[1] += px[o + 1] / 4;
            ground[2] += px[o + 2] / 4;
          });
          var solid = function (x, y) {
            var o = (y * w + x) * 4;
            if (px[o + 3] < 40) return false;
            var dr = px[o] - ground[0];
            var dg = px[o + 1] - ground[1];
            var db = px[o + 2] - ground[2];
            return dr * dr + dg * dg + db * db > 900;
          };
          var x0 = w, x1 = -1, y0 = h, y1 = -1;
          for (var y = 0; y < h; y++) {
            for (var x = 0; x < w; x++) {
              if (!solid(x, y)) continue;
              if (x < x0) x0 = x;
              if (x > x1) x1 = x;
              if (y < y0) y0 = y;
              if (y > y1) y1 = y;
            }
          }
          var bw = x1 - x0 + 1;
          var bh = y1 - y0 + 1;
          if (x1 < 0 || bw < bh * 1.25) { done(null, true); return; }
          // The highest row the outline reaches within each end strip.
          var strip = Math.max(2, Math.round(bw * 0.22));
          var need = Math.max(1, Math.round(strip * 0.1));
          var topOf = function (from, to) {
            for (var yy = y0; yy <= y1; yy++) {
              var hits = 0;
              for (var xx = from; xx <= to; xx++) if (solid(xx, yy)) hits++;
              if (hits >= need) return yy;
            }
            return y1;
          };
          var leftTop = topOf(x0, x0 + strip);
          var rightTop = topOf(x1 - strip, x1);
          // The heel stands taller, so the toe is the lower end. A flat
          // sandal's ends are level: it is still sized and set on the
          // baseline, just never flipped on a guess.
          var toe = null;
          if (Math.abs(leftTop - rightTop) >= bh * 0.12) toe = leftTop < rightTop ? 'right' : 'left';
          done({
            aspect: w / h,
            box: [x0 / w, y0 / h, (x1 + 1) / w, (y1 + 1) / h],
            toe: toe
          }, true);
        } catch (e) {
          done(null, false);
        }
      };
      img.src = smallCopy(src, 96);
    });
  }

  /* The transform that frames this shoe, or '' to leave the plate as it is.
     Worked in plate units (width 1, height PLATE_RATIO) for an image laid out
     with object-fit: contain and transform-origin 0 0. */
  function shoeFrame(geo) {
    if (!geo) return '';
    var H = PLATE_RATIO;
    var dw, dh, ox, oy;
    if (geo.aspect > 1 / H) {
      dw = 1; dh = 1 / geo.aspect; ox = 0; oy = (H - dh) / 2;
    } else {
      dh = H; dw = H * geo.aspect; ox = (1 - dw) / 2; oy = 0;
    }
    var b = geo.box;
    var pw = (b[2] - b[0]) * dw;
    var ph = (b[3] - b[1]) * dh;
    var want = document.documentElement.getAttribute('dir') === 'rtl' ? 'left' : 'right';
    var flip = !!geo.toe && geo.toe !== want;
    var px0 = ox + b[0] * dw;
    if (flip) px0 = 1 - (px0 + pw);
    var py0 = oy + b[1] * dh;
    var scale = Math.min(SHOE_WIDTH / pw, (SHOE_MAX_HEIGHT * H) / ph);
    var tx = 0.5 - scale * (px0 + pw / 2);
    var ty = SHOE_BASELINE * H - scale * (py0 + ph);
    var t = 'translate(' + (tx * 100).toFixed(2) + '%, ' + (ty / H * 100).toFixed(2) + '%) scale(' + scale.toFixed(4) + ')';
    return flip ? t + ' translateX(100%) scaleX(-1)' : t;
  }

  function shoeFrameFor(card, src) {
    if (!src || !isFootwear(card)) return Promise.resolve('');
    return shoeGeometry(src).then(shoeFrame);
  }

  function setFrame(img, frame) {
    img.classList.toggle('is-framed', !!frame);
    if (frame) img.style.setProperty('--bs-shoe-frame', frame);
    else img.style.removeProperty('--bs-shoe-frame');
  }

  function frameShoe(card, img, src) {
    if (!src || !isFootwear(card)) return Promise.resolve();
    // A complete worn studio photograph already owns its crop and orientation.
    if (img.closest('[data-spdept-studio="lifestyle"]')) return Promise.resolve();
    img.classList.add('is-framing');
    return shoeFrameFor(card, src).then(function (frame) {
      setFrame(img, frame);
      img.classList.remove('is-framing');
    });
  }

  /* Runs fn once the card is on, or about to come on, screen. Ranking a
     card's candidates downloads a thumbnail of each, so it waits for the card
     to be seen: a card in a closed tab or far along a rail costs nothing
     until the shopper gets there. Without IntersectionObserver it runs at
     once, as before. */
  var nearObserver = null;
  var nearJobs = typeof WeakMap === 'function' ? new WeakMap() : null;

  function whenNear(card, fn) {
    if (!window.IntersectionObserver || !nearJobs) { fn(); return; }
    if (!nearObserver) {
      nearObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var job = nearJobs.get(entry.target);
          nearObserver.unobserve(entry.target);
          nearJobs.delete(entry.target);
          if (job) job();
        });
      }, { rootMargin: '200px 400px' });
    }
    nearJobs.set(card, fn);
    nearObserver.observe(card);
  }

  function fill(card, data, seen, eager) {
    /* A probe that never answered is NOT a category that came back thin, and
       the two must not share a branch. They did once, and a rate-limited tab
       deleted every card in it: Footwear disappeared with 60 sneakers behind
       it, and markEmpty then hid the tab as well. The card is kept — its link
       was built in Liquid and works without a count — so a failed request
       costs a count and a photograph, never the category itself. */
    if (!data) {
      card.classList.remove('bs-spdept__card--pending');
      return true;
    }

    if (data.count < card.__min) {
      /* Below the floor. Removed rather than left blank — an empty plate in
         the middle of a run is worse than a shorter run. */
      card.parentNode.removeChild(card);
      return false;
    }

    var count = card.querySelector('[data-spdept-count]');
    if (count) count.textContent = countLabel(card.__root, data.count);

    /* Choosing the photograph means measuring it, so the card is finished now
       and the image arrives a moment later — the plate already holds its
       proportion, so nothing reflows when it lands. */
    /* A pinned photograph is already in the markup (see bs-spdept-rail) and is
       the merchant's explicit choice, so the picker never overwrites it.

       It IS claimed in the `seen` ledger, though. It used to be left out, on
       the reasoning that claiming it would push a sibling off a photograph it
       could otherwise have used — but the opposite is what happens: the picker
       does not know the photograph is spoken for and hands the identical image
       to the next card along. Pinning Men's Hair Care put the same shower kit
       on Hair Care beside it. Claimed by URL rather than product id, because
       a pin is a bare URL and carries no id. */
    var pinned = card.querySelector('[data-spdept-media][data-spdept-pinned]');
    if (pinned) {
      var pinnedImg = pinned.querySelector('img');
      if (pinnedImg) {
        seen[imageKey(null, pinnedImg.getAttribute('src'))] = true;
        whenNear(card, function () { frameShoe(card, pinnedImg, pinnedImg.getAttribute('src')); });
      }
      card.classList.remove('bs-spdept__card--pending');
      return true;
    }


    var media = card.querySelector('[data-spdept-media]');
    if (!media) return true;

    var prerendered = card.querySelector('[data-spdept-media][data-spdept-prerendered]');
    var img = prerendered && prerendered.querySelector('img');
    if (img) seen[imageKey(null, img.getAttribute('src'))] = true;

    /* Put a usable image on the card as soon as the JSON arrives. The visual
       chooser can improve it later, but crawlers and fast-scrolling shoppers
       no longer wait for up to 24 canvas probes before an <img> exists. */
    var firstImage = null;
    var available = data.images || [];
    for (var firstIndex = 0; firstIndex < available.length; firstIndex++) {
      var candidate = available[firstIndex];
      var candidateProductKey = candidate.id != null ? 'p' + candidate.id : null;
      var candidateFileKey = imageKey(null, candidate.src);
      if ((!candidateProductKey || !seen[candidateProductKey]) && !seen[candidateFileKey]) {
        firstImage = candidate;
        if (candidateProductKey) seen[candidateProductKey] = true;
        seen[candidateFileKey] = true;
        break;
      }
    }
    if (!img) img = document.createElement('img');
    if (!img.parentNode && firstImage && firstImage.src) {
      img.src = absoluteImageUrl(firstImage.src);
      img.alt = card.getAttribute('data-label') || '';
      img.loading = eager ? 'eager' : 'lazy';
      img.width = 600;
      img.height = 750;
      media.insertBefore(img, media.firstChild);
    }

    /* The first image is claimed now, synchronously (see pickImage), so a
       sibling that ranks first can never take the same product. */
    var claimFirst = firstImage;
    whenNear(card, function () { rank(claimFirst); });

    function rank(first) {
      pickImage(seen, data, first).then(function (result) {
        if (!result || !result.image) return;
        if (!img.parentNode) media.insertBefore(img, media.firstChild);
        var nextSrc = absoluteImageUrl(result.image.src);
        if (img.getAttribute('src') !== nextSrc) {
          /* Swapped for a richer photograph: load it off-screen first, then
             cross-fade, so the plate never flashes empty or jumps. */
          var preload = new Image();
          var loaded = new Promise(function (resolve) {
            preload.onload = function () { resolve(true); };
            preload.onerror = function () { resolve(false); };
          });
          preload.src = nextSrc;
          /* A shoe's framing is settled while the new photograph is still
             hidden, so it never visibly jumps or flips after landing. */
          Promise.all([loaded, shoeFrameFor(card, nextSrc)]).then(function (state) {
            if (!state[0]) {
              img.src = nextSrc;
              setFrame(img, state[1]);
              return;
            }
            img.classList.add('is-swapping');
            window.setTimeout(function () {
              img.src = nextSrc;
              setFrame(img, state[1]);
              img.classList.remove('is-swapping');
            }, 160);
          });
        } else {
          frameShoe(card, img, nextSrc);
        }

        /* Measured pale: nothing darker was available, so the ground stays and
           the photograph is pushed harder instead. */
        if (result.ink !== null && result.ink < INK_ENOUGH) {
          media.classList.add('bs-spdept__media--pale');
        }
      });
    }

    card.classList.remove('bs-spdept__card--pending');
    return true;
  }

  /* ---- Rail ----------------------------------------------------------- */

  /* A rail inside a hidden panel measures zero, so every arrow would read as
     "nothing further to scroll" until its tab was opened. State is recomputed
     on tab change and as cards land, as well as on scroll and resize. */
  function syncArrows(rail) {
    var track = rail.querySelector('[data-spdept-track]');
    var prev = rail.querySelector('[data-spdept-prev]');
    var next = rail.querySelector('[data-spdept-next]');
    if (!track || !prev || !next) return;

    var max = track.scrollWidth - track.clientWidth;
    /* A pixel of slack: browsers land fractionally short of the end when the
       track width is not a whole number, which it rarely is here. */
    var rtl = window.getComputedStyle(track).direction === 'rtl';
    var at = Math.round(rtl ? -track.scrollLeft : track.scrollLeft);

    /* A run that fits has nothing to scroll, and the rail is full width
       whether it holds three cards or thirty — so leaving the controls in
       place hangs two buttons in the empty space past the last card, a long
       way from anything they could act on. They are taken off the rail
       entirely rather than merely disabled. */
    var scrollable = max > 1;
    prev.hidden = !scrollable;
    next.hidden = !scrollable;
    if (!scrollable) return;

    prev.disabled = at <= 1;
    next.disabled = at >= max - 1;
  }

  function setUpRail(rail) {
    var track = rail.querySelector('[data-spdept-track]');
    if (!track) return;

    var prev = rail.querySelector('[data-spdept-prev]');
    var next = rail.querySelector('[data-spdept-next]');

    function step(direction) {
      var rtl = window.getComputedStyle(track).direction === 'rtl';
      track.scrollBy({
        left: Math.max(track.clientWidth * SCROLL_SHARE, 240) * direction * (rtl ? -1 : 1),
        behavior: 'smooth'
      });
    }

    if (prev) prev.addEventListener('click', function () { step(-1); });
    if (next) next.addEventListener('click', function () { step(1); });

    track.addEventListener('scroll', function () {
      syncArrows(rail);
    }, { passive: true });

    syncArrows(rail);
  }

  /* ---- Panels --------------------------------------------------------- */

  function load(root, panel) {
    if (panel.dataset.spdeptLoaded === 'true') return;
    panel.dataset.spdeptLoaded = 'true';

    var min = parseInt(root.getAttribute('data-min-products'), 10) || 1;
    var rail = panel.querySelector('[data-spdept-rail]');
    var cards = list(panel, '[data-spdept-card]');
    if (cards.length === 0) return;

    /* Claim every pinned photograph before a single probe goes out. Doing it
       in fill(), as each card's own request lands, is too late: the cards race,
       and an unpinned neighbour that answers first has already chosen the photo
       the pin was about to claim. */
    list(panel, '[data-spdept-media][data-spdept-pinned] img').forEach(function (img) {
      root.__seen[imageKey(null, img.getAttribute('src'))] = true;
    });

    /* The first row a shopper sees is the page's largest paint, so its
       photographs are fetched eagerly; everything below stays lazy. */
    var eagerBudget = 6;
    var kept = 0;

    var jobs = cards.map(function (card) {
      card.__min = min;
      /* The translated count forms live on the section root. */
      card.__root = root;
      return function () {
        /* Each card names the collection its own tag lives in: a department
           pooling a shared collection holds cards from more than one. */
        var url = card.getAttribute('data-collection-url');
        if (!url) return Promise.resolve();
        var categoryId = card.getAttribute('data-sale-category');
        /* The build baked this card's count and photograph into the markup,
           so there is nothing to fetch: the photograph is treated as a pin. */
        var baked = card.getAttribute('data-spdept-baked');
        if (baked) {
          var eagerBaked = eagerBudget > 0;
          if (fill(card, { count: parseInt(baked, 10) || 0, images: [] }, root.__seen, eagerBaked)) {
            kept++;
            if (eagerBaked) eagerBudget--;
          }
          if (rail) syncArrows(rail);
          return Promise.resolve();
        }
        var categoryProbe = categoryId && window.SaleCategoryIndex ? window.SaleCategoryIndex.probe(categoryId) : probe(url, card.getAttribute('data-handle'));
        return categoryProbe.then(
          function (data) {
            var eager = eagerBudget > 0;
            if (fill(card, data, root.__seen, eager)) {
              kept++;
              if (eager) eagerBudget--;
            }
            if (rail) syncArrows(rail);
          },
          function () {
            fill(card, null, root.__seen, false);
            if (rail) syncArrows(rail);
          }
        );
      };
    });

    return pool(jobs, CONCURRENCY).then(function () {
      if (kept === 0) markEmpty(panel);
      if (rail) syncArrows(rail);
    });
  }

  /* A tab whose every category came back under the floor is not worth opening,
     so it stops being a tab. Emptiness is only knowable once the counts land —
     no page template can filter a collection by tag — which is why this is
     removal after the fact rather than a tab that was never built. */
  function markEmpty(panel) {
    panel.classList.add('is-empty');

    var dept = panel.closest('[data-spdept-dept]');
    if (!dept) return;

    var button = dept.querySelector('[data-spdept-tab="' + panel.id + '"]');
    if (!button) return;

    button.hidden = true;

    /* If the tab that emptied was the one on screen, move to the first that
       still has something behind it rather than leaving a blank department. */
    if (button.getAttribute('aria-selected') !== 'true') return;

    var next = list(dept, '[data-spdept-tab]').filter(function (tab) {
      return !tab.hidden;
    })[0];
    if (next) activate(dept, next);
    else hideDept(dept);
  }

  /* Class rules on the section beat the [hidden] default, so set display. */
  function hideDept(dept) {
    dept.hidden = true;
    dept.style.display = 'none';
  }

  /* A tab strip that overflows: keep the chosen tab whole and in view, and
     tell the strip which edges still hide tabs so it can fade them. */
  function syncTabEdges(strip) {
    if (!strip) return;
    var max = strip.scrollWidth - strip.clientWidth;
    var rtl = window.getComputedStyle(strip).direction === 'rtl';
    var at = Math.round(rtl ? -strip.scrollLeft : strip.scrollLeft);
    strip.classList.toggle('has-more-before', max > 1 && at > 1);
    strip.classList.toggle('has-more-after', max > 1 && at < max - 1);
  }

  function showTab(button) {
    var strip = button.closest('[data-spdept-tabs]');
    if (!strip) return;
    if (strip.scrollWidth - strip.clientWidth > 1) {
      var edge = button.getBoundingClientRect();
      var box = strip.getBoundingClientRect();
      /* A little past the edge, so the next tab peeks and the row reads as
         scrollable rather than ending there. */
      var room = 24;
      if (edge.left < box.left + room) strip.scrollBy({ left: edge.left - box.left - room, behavior: 'smooth' });
      else if (edge.right > box.right - room) strip.scrollBy({ left: edge.right - box.right + room, behavior: 'smooth' });
    }
    window.setTimeout(function () { syncTabEdges(strip); }, 320);
  }

  function activate(dept, button) {
    var panelId = button.getAttribute('data-spdept-tab');

    list(dept, '[data-spdept-tab]').forEach(function (tab) {
      var on = tab === button;
      tab.classList.toggle('is-active', on);
      tab.setAttribute('aria-selected', on ? 'true' : 'false');
      /* Roving tabindex: one stop for the whole tablist, arrow keys move
         within it. */
      tab.tabIndex = on ? 0 : -1;
    });

    /* The tab strip scrolls sideways once a department has more tabs than
       fit, and the chosen one was often left half-cut at an edge — the row
       keeps whatever scroll position the previous tab left it at. Bring it
       fully into the strip, and mark the strip so the CSS can fade whichever
       edge still has tabs behind it. */
    showTab(button);

    list(dept, '[data-spdept-panel]').forEach(function (panel) {
      var on = panel.id === panelId;
      panel.hidden = !on;
      if (!on) return;

      load(dept.__root, panel);
      var rail = panel.querySelector('[data-spdept-rail]');
      if (rail) syncArrows(rail);
    });
  }

  function setUpDept(root, dept) {
    dept.__root = root;

    list(dept, '[data-spdept-rail]').forEach(setUpRail);

    list(dept, '[data-spdept-tabs]').forEach(function (strip) {
      strip.addEventListener('scroll', function () { syncTabEdges(strip); }, { passive: true });
      if ('ResizeObserver' in window) new ResizeObserver(function () { syncTabEdges(strip); }).observe(strip);
      else window.addEventListener('resize', function () { syncTabEdges(strip); }, { passive: true });
      syncTabEdges(strip);
    });

    /* The open panel first, because it is what a shopper is looking at. The
       closed ones follow once the browser is idle: a tab has to be resolved to
       know whether it is empty, and an empty tab that only reveals itself when
       clicked is worse than the requests it costs to find out early. */
    var panels = list(dept, '[data-spdept-panel]');
    var open = panels.filter(function (panel) { return !panel.hidden; })[0];

    function rest() {
      panels.forEach(function (panel) {
        if (panel !== open) load(root, panel);
      });
    }

    if (open) {
      var started = load(root, open);
      if (started && started.then) started.then(rest);
      else rest();
    } else {
      rest();
    }

    var tabs = list(dept, '[data-spdept-tab]');
    if (tabs.length === 0) return;

    tabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        activate(dept, tab);
      });
    });

    var tablist = dept.querySelector('[role="tablist"]');
    if (!tablist) return;

    tablist.addEventListener('keydown', function (event) {
      var available = tabs.filter(function (tab) { return !tab.hidden; });
      var current = available.indexOf(document.activeElement);
      if (current === -1) return;

      var target = null;
      if (event.key === 'ArrowRight') target = (current + 1) % available.length;
      if (event.key === 'ArrowLeft') target = (current - 1 + available.length) % available.length;
      if (event.key === 'Home') target = 0;
      if (event.key === 'End') target = available.length - 1;
      if (target === null) return;

      event.preventDefault();
      available[target].focus();
      activate(dept, available[target]);
    });
  }

  function setUp(root) {
    /* Product ids already spent on a card, shared across the whole section so
       two departments cannot lead with the same photograph. */
    root.__seen = {};

    var floor = parseInt(root.getAttribute('data-min-products'), 10) || 1;
    list(root, '[data-spdept-dept]').forEach(function (dept) {
      /* A department with nothing on offer (no cards, or every card baked
         under the minimum) is left off the page instead of showing an empty
         heading - Luggage & Travel when no luggage is reduced. */
      var cards = list(dept, '[data-spdept-card]');
      var bakedEmpty = cards.length > 0 && cards.every(function (card) {
        var baked = card.getAttribute('data-spdept-baked');
        return baked !== null && (parseInt(baked, 10) || 0) < floor;
      });
      if (cards.length === 0 || bakedEmpty) {
        hideDept(dept);
        return;
      }
      setUpDept(root, dept);
    });

    var resync = function () {
      list(root, '[data-spdept-rail]').forEach(syncArrows);
    };

    window.addEventListener('resize', resync, { passive: true });
    /* Contained images settle late — a track's scrollWidth is not final until
       the first run of art has decoded. */
    window.addEventListener('load', resync);
  }

  function init() {
    list(document, '[data-spdept]').forEach(function (root) {
      if (root.dataset.spdeptReady === 'true') return;
      root.dataset.spdeptReady = 'true';
      setUp(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* The theme editor replaces the section's markup wholesale on every setting
     change, which drops the listeners with it. */
  document.addEventListener('shopify:section:load', init);
})();

/* Card grounds follow each product photo. A shot on white melts into the
   warm-grey ground (multiply, in the stylesheet). A shot on its own plain
   studio colour - mid grey, beige - would sit on that ground as a darker box
   wherever the photo is shown whole (shoes are framed, not cropped), so the
   ground takes the photo's own corner colour and the blend is switched off.
   Lifestyle shots (#bs-life) are left as they are. Reads a 24px copy of the
   photo; the CDN serves it with open CORS. */
(function () {
  'use strict';

  function sample(img) {
    var media = img.closest('.bs-spdept__media');
    if (!media) return;
    // Studio rails share the PLP grey and multiply treatment. Do not replace
    // that common ground with a separate sampled colour for each photograph.
      if (media.hasAttribute('data-spdept-studio')) return;
    if (/#bs-life/.test(img.getAttribute('src') || '')) return;
    var src = img.currentSrc || img.src;
    if (!src || media.__toneFor === src) return;
    media.__toneFor = src;
    // A swapped-in photo starts from the shared treatment again.
    media.style.backgroundColor = '';
    img.style.mixBlendMode = '';
    var probe = new Image();
    probe.crossOrigin = 'anonymous';
    probe.onload = function () {
      try {
        var w = probe.naturalWidth, h = probe.naturalHeight;
        var canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        var ctx = canvas.getContext('2d');
        ctx.drawImage(probe, 0, 0);
        var corners = [[1, 1], [w - 2, 1], [1, h - 2], [w - 2, h - 2]].map(function (p) {
          return ctx.getImageData(p[0], p[1], 1, 1).data;
        });
        var avg = [0, 1, 2].map(function (c) {
          return Math.round(corners.reduce(function (s, px) { return s + px[c]; }, 0) / 4);
        });
        var spread = Math.max.apply(null, corners.map(function (px) {
          return Math.max(Math.abs(px[0] - avg[0]), Math.abs(px[1] - avg[1]), Math.abs(px[2] - avg[2]));
        }));
        var light = Math.min(avg[0], avg[1], avg[2]);
        if (media.__toneFor !== src) return;
        // White ground, or a busy edge: keep the shared grey treatment.
        if (light >= 240 || spread > 14 || light < 150) return;
        media.style.backgroundColor = 'rgb(' + avg.join(',') + ')';
        img.style.mixBlendMode = 'normal';
      } catch (e) { /* unreadable image: keep the shared treatment */ }
    };
    probe.src = src.replace(/([?&])width=\d+/, '$1width=24').replace(/#.*$/, '') + (/[?&]width=/.test(src) ? '' : (src.indexOf('?') > -1 ? '&' : '?') + 'width=24');
  }

  document.addEventListener('load', function (event) {
    var img = event.target;
    if (img && img.tagName === 'IMG' && img.closest && img.closest('.bs-spdept__media')) sample(img);
  }, true);

  Array.prototype.forEach.call(document.querySelectorAll('.bs-spdept__media img'), function (img) {
    if (img.complete && img.naturalWidth) sample(img);
  });
})();
